// src/pages/Settings.jsx
// Settings (3.29.2; titled just “Settings” from 3.30.5, the Console has its own Fax Settings), in the top menu for every client — how
// automatic retries work for the client, its ★ priority numbers, and its
// retry lists (scheduled, exhausted or cancelled, delivered by a retry),
// moved here from the Console. Lists follow the Outbox rule (Users their
// own faxes, Managers and Admins their client's, Super Admins every
// client's on "All clients"). The on/off switches stay in the Console.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import SortTh, { useSort } from '../components/SortTh';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import EmptyState from '../components/EmptyState';
import { formatFax, formatDate } from '../utils/format';
import { PAGE_TITLE, SECTION_SUB, PILL, PILL_ON, PILL_ROW, TAB, TAB_ON, TAB_ROW } from '../theme/tabs';
import * as SS from './SettingsSections';
import Loader from '../components/Loader';
import Emo, { emoLabel } from '../components/Emo';
import Reminders from './Reminders';
import { usePhone } from '../hooks/useWindowWidth';   // 3.49.18: moved here from Users
import { useClientWord } from '../hooks/useClientWord';
import { isSuperAdmin } from '../utils/account';
import { useSendTimings } from '../hooks/useSendTimings';

const last10 = n => String(n || '').replace(/\D/g, '').slice(-10);

// Settings (3.30.0): sub tabs — Fax Retries (3.29.2) and the client's
// own settings (pages/SettingsSections), each shown to the roles allowed it
// in Roles & Access. Super Admins on "All clients" pick the client first
// for the client settings.
// 3.61.0 (owner): Fax Settings (opened first) groups Fax Retries, Incoming
// Fax Emails, Fax Lines, Cover Sheet and Default Line in its own row; Office
// Hours is new; the rest stay their own tabs.
const SECTIONS = [
  { id: 'incomingEmails', label: 'Incoming Fax Emails', icon: '📨', C: SS.IncomingEmails, fax: true },
  { id: 'lineNames', label: 'Fax Lines', icon: '📞', C: SS.LineNames, fax: true },
  { id: 'coverSheet', label: 'Cover Sheet', icon: '📄', C: SS.CoverSheet, fax: true },
  { id: 'defaultLine', label: 'Default Line', icon: '🎯', C: SS.DefaultLine, fax: true },
  { id: 'sendConfirm', label: 'Send Confirmation', icon: '🛡️', C: SS.SendConfirm, fax: true },   // 3.64.0 (owner)
  { id: 'officeHours', label: 'Office Hours', icon: '🕘', C: SS.OfficeHours },
  { id: 'blockedNumbers', label: 'Blocked Numbers', icon: '⛔', C: SS.BlockedNumbers },
  { id: 'notices', label: 'Notification Emails', icon: '✉️', C: SS.Notices },
];
// 3.62.9 (owner): a short line under Fax Settings, like Reminders has, and
// one under each of its parts (Fax Retries keeps its own, in FaxRetries)
const FAX_INTRO = 'Everything about sending and receiving faxes for your company: how failed faxes are retried, who gets received faxes by email, what your fax lines are called, the cover sheet, the line Compose starts on, and checking before a fax goes.';
const FAX_PART_ABOUT = {
  incomingEmails: "Who gets an email when a fax arrives: your company's list, each team's own list, and whether everyone in a team gets the faxes sent to the team's number.",
  lineNames: 'Names for your fax numbers, so people can tell your lines apart wherever they pick one, like Compose → Send from.',
  coverSheet: 'The cover sheet Compose starts with for everyone, and the details that faxes sent by email use on their automatic cover sheet.',
  defaultLine: 'The fax line Compose starts on for you. It changes only your own Compose, and you can still pick another line for each fax.',
  sendConfirm: 'Before a fax from Compose goes out, the sender confirms it’s going to the right receiver: the right name and fax number. It stops documents going to the wrong number.',
  sendTimings: 'How long faxes to the same number wait for each other, when and how often failed faxes are retried, and when retries pause or a person is blocked. One set for every client.',
};
export default function Settings() {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const { getToken, can, accountVersion, effectiveInfo } = useAuth();
  const { notify } = useNotify();
  const allClients = effectiveInfo?.accountId === 'all';
  const [pick, setPick] = useState('');               // Super Admins on All clients: which client
  const [client, setClient] = useState(null);         // /settings/client answer
  const [saving, setSaving] = useState('');
  const [tab, setTab] = useState(() => { try { return window.sessionStorage.getItem('faxPortal.settingsTab') || 'retries'; } catch { return 'retries'; } });
  const loadClient = useCallback(async () => {
    if (allClients && !pick) { setClient({ sections: {} }); return; }
    try {
      const r = await fetch(`${API_BASE}/portal/settings/client${pick ? `?account=${encodeURIComponent(pick)}` : ''}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      setClient(r.ok ? j : { sections: {} });
    } catch { setClient({ sections: {} }); }
  }, [getToken, pick, allClients]);
  useEffect(() => { loadClient(); }, [loadClient, accountVersion]);
  // The parts of Fax Settings, and the tabs beside it
  const faxParts = [
    ...(can('settings.retries') ? [{ id: 'retries', label: 'Fax Retries', icon: '🔁' }] : []),
    ...SECTIONS.filter(x => x.fax && can(`settings.${x.id}`)),
    // 3.75.0 (owner): Super Admins only (not while viewing as someone); every client's
    ...(isSuperAdmin(effectiveInfo) ? [{ id: 'sendTimings', label: 'Sending Timings', icon: '⏱️' }] : []),
  ];
  const tabs = [
    ...(faxParts.length ? [{ id: 'fax', label: 'Fax Settings', icon: '📠' }] : []),
    ...SECTIONS.filter(x => !x.fax && can(`settings.${x.id}`)),
    // 3.49.18 (owner): Reminders, next to Notification Emails (it was under Users)
    ...(can('reminders.view') ? [{ id: 'reminders', label: 'Reminders', icon: '⏰' }] : []),
  ];
  // `tab` is the part shown: a Fax Settings part, or one of the other tabs
  const inFax = faxParts.some(t => t.id === tab);
  const top = inFax ? 'fax' : tabs.find(t => t.id === tab && t.id !== 'fax') ? tab : tabs[0]?.id;
  const current = top === 'fax' ? (inFax ? tab : faxParts[0]?.id) : top;
  function open(id) { setTab(id); try { window.sessionStorage.setItem('faxPortal.settingsTab', id); } catch { /* not kept */ } }
  async function save(section, body) {
    setSaving(section);
    try {
      const r = await fetch(`${API_BASE}/portal/settings/client/${section}`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, ...(pick ? { accountId: pick } : {}) }) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't save that.");
      notify({ tone: 'success', title: 'Saved', detail: section === 'defaultLine' ? 'Compose starts on it from your next fax (sign in again if it doesn’t yet).' : undefined });
      await loadClient();
    } catch (e) { notify({ tone: 'error', title: "Couldn't save", detail: e.message }); }
    setSaving('');
  }
  const section = SECTIONS.find(x => x.id === current);
  const sectionData = section && client?.sections?.[section.id];
  return (
    <div>
      <h2 style={{ ...PAGE_TITLE, margin: '0 0 10px' }} className="cp-title"><Emo e="🛠️" /> Settings</h2>
      {/* 3.61.0: the Console's two rows (theme/tabs.js): tabs, then Fax Settings' parts as pills */}
      <div style={{ ...TAB_ROW, marginBottom: top === 'fax' && faxParts.length > 1 ? 12 : 6 }} role="tablist" aria-label="Settings">
        {tabs.map(t => (
          <button key={t.id} role="tab" aria-selected={top === t.id} onClick={() => open(t.id === 'fax' ? faxParts[0].id : t.id)} style={{ ...TAB, ...(top === t.id ? TAB_ON : {}) }}>
            <Emo e={t.icon} /> {t.label}
          </button>
        ))}
      </div>
      {top === 'fax' && (
        <div style={{ margin: '4px 0 12px' }}>
          <h3 style={S.groupTitle}>Fax Settings</h3>
          <p style={S.groupSub}>{FAX_INTRO}</p>
        </div>
      )}
      {top === 'fax' && faxParts.length > 1 && (
        <div style={{ ...PILL_ROW, marginBottom: 6 }} role="tablist" aria-label="Fax settings">
          {faxParts.map(t => (
            <button key={t.id} role="tab" aria-selected={current === t.id} onClick={() => open(t.id)} style={{ ...PILL, ...(current === t.id ? PILL_ON : {}) }}>
              <Emo e={t.icon} /> {t.label}
            </button>
          ))}
        </div>
      )}
      {top === 'fax' && FAX_PART_ABOUT[current] && <p style={SECTION_SUB}>{FAX_PART_ABOUT[current]}</p>}
      {!tabs.length && <p style={S.sub}>Your role has no fax settings to show. Ask your Admin.</p>}
      {current === 'retries' && <FaxRetries />}
      {current === 'sendTimings' && <SS.SendingTimings />}
      {current === 'reminders' && <div style={{ marginTop: 12 }}><Reminders /></div>}
      {section && allClients && (
        <div style={{ margin: '12px 0 0', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--t-37474f)' }}>{clientWord}</span>
          <select value={pick} onChange={e => { setPick(e.target.value); setClient(null); }} style={S.select}>
            <option value="">Pick a client…</option>
            {(effectiveInfo?.accounts || []).filter(a => a.id !== 'all').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      )}
      {section && (allClients && !pick ? <p style={{ ...S.sub, marginTop: 10 }}>Pick a client to see and change its {section.label.toLowerCase()}.</p>
        : !client ? <Loader label="Loading settings…" />
        : sectionData ? <section.C key={`${client.accountId}:${section.id}:${JSON.stringify(sectionData).length}`} data={sectionData} saving={saving === section.id} save={body => save(section.id, body)} />
        : <p style={S.sub}>Nothing to show here.</p>)}
    </div>
  );
}

function FaxRetries() {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const tm = useSendTimings();   // 3.75.0: Sending Timings, as words
  // "one automatic retry" / "2 automatic retries" / "no automatic retry"
  const retryWords = tm.retries === 0 ? 'no automatic retry' : tm.retries === 1 ? 'one automatic retry' : `${tm.retries} automatic retries`;
  const { getToken, can, shows, accountVersion, effectiveInfo } = useAuth();
  const phone = usePhone();   // 3.51.0: the lists as cards on phones
  const { notify, confirm } = useNotify();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [list, setList] = useState('pending');
  const [show, setShow] = useState('50');
  const load = useCallback(async () => {
    setError('');
    try {
      const r = await fetch(`${API_BASE}/portal/settings/fax`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't load the fax settings.");
      setData(j);
    } catch (e) { setError(e.message); }
  }, [getToken]);
  useEffect(() => { load(); }, [load, accountVersion]);
  // Retry log (3.49.22): read only when its tab is opened
  const [log, setLog] = useState(null);
  const loadLog = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/portal/settings/fax/log`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't load the retry log.");
      setLog(j);
    } catch (e) { setLog({ rows: [], error: e.message }); }
  }, [getToken]);
  useEffect(() => { setLog(null); }, [accountVersion]);
  useEffect(() => { if (list === 'log' && !log) loadLog(); }, [list, log, loadLog]);

  async function call(path, body, okTitle, key) {
    setBusy(key);
    try {
      const r = await fetch(`${API_BASE}${path}`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'That didn’t work.');
      notify({ bell: 'fax', tone: 'success', title: okTitle, ...(j.waiting ? { detail: 'That number is busy with another fax; it goes out by itself when the line is free.' } : {}) });
      await load();
      setLog(null);   // the Retry log reloads when next opened
    } catch (e) { notify({ tone: 'error', title: "Couldn't do that", detail: e.message }); }
    setBusy('');
  }
  async function cancelRetry(r) {
    if (!(await confirm({ title: 'Cancel this retry?', message: `The fax to ${formatFax(r.toNumber)} stays failed and won’t be retried automatically. You can still resend it.`, confirmLabel: 'Cancel retry', cancelLabel: 'Keep it', danger: true }))) return;
    call(`/portal/fax/outgoing/${encodeURIComponent(r.faxId)}/cancel-retry`, null, 'Retry cancelled', `c:${r.faxId}`);
  }
  // 3.49.22: send a fax waiting for its automatic retry now (the retry is cancelled)
  async function resendNow(r) {
    if (!(await confirm({ title: 'Resend this fax now?', message: `It goes to ${formatFax(r.toNumber)} now (${r.pages ?? '?'} pages) instead of waiting for the automatic retry at ${formatDate(r.fireAt)}. If that number is busy with another fax, it goes out as soon as the line is free. It gets ${retryWords} if it fails.`, confirmLabel: 'Resend now' }))) return;
    call(`/portal/resend/${encodeURIComponent(r.faxId)}`, null, 'Fax sent again', `r:${r.faxId}`);
  }
  // 3.69.4 (owner): a Resend waiting for the line goes out now
  async function sendNow(r) {
    if (!(await confirm({ title: 'Send this fax now?', message: `It goes to ${formatFax(r.toNumber)} now instead of waiting in line${r.slow ? `. This number is hard to reach, so faxes to it are normally spaced ${tm.hardSpacing} apart` : ''}. If a fax to that number is on the line right now, try again in a few minutes.`, confirmLabel: 'Send now' }))) return;
    call(`/portal/outgoing/waiting/${encodeURIComponent(r.queueId)}/send-now`, null, 'Fax sent', `s:${r.queueId}`);
  }
  async function resend(r) {
    if (!(await confirm({ title: 'Resend this fax?', message: `It goes to ${formatFax(r.toNumber)} again (${r.pages ?? '?'} pages), and gets ${retryWords} if it fails.`, confirmLabel: 'Resend' }))) return;
    call(`/portal/resend/${encodeURIComponent(r.faxId)}`, null, 'Fax sent again', `r:${r.faxId}`);
  }

  const allClients = !!data?.allClients;
  const lists = useMemo(() => ({
    // 3.69.4 (owner): + Resends waiting for the line, at their rough time
    pending: [...(data?.pending || []), ...(data?.inLine || []).map(w => ({ ...w, line: true, fireAt: new Date(Date.now() + (w.etaMin || 0) * 60000).toISOString(), nextAttempt: 'Resend' }))],
    exhausted: data?.exhausted || [], recovered: data?.recovered || [], log: log?.rows || [],
  }), [data, log]);
  const rows = lists[list];
  const { sorted, sort, onSort } = useSort(rows, {
    when: r => (list === 'log' ? r.at : list === 'pending' ? r.fireAt : list === 'recovered' ? r.deliveredAt : (r.updatedAt || r.sentAt)) || '',
    client: r => r.accountName || '', by: r => (list === 'log' ? r.who : r.fromEmail) || '', what: r => LOG_WHAT(r), result: r => r.result || '', to: r => last10(r.toNumber), file: r => r.fileName || '',
    pages: r => Number(r.pages) || 0, reason: r => (list === 'recovered' ? r.firstFailure : r.failureReason) || '', attempt: r => r.attempt || r.nextAttempt || 0,
  });
  const { pageRows, pager } = usePager(sorted, show);

  if (!data) return (
    <div>
      {error ? <div style={S.error}>{error}</div> : <Loader label="Loading settings…" />}
    </div>
  );
  const clientName = allClients ? 'every client' : (effectiveInfo?.groupLabel || 'your company');
  const whenLabel = list === 'log' ? 'When' : list === 'pending' ? 'Retry at' : list === 'recovered' ? 'Delivered' : 'Last tried';
  return (
    <div>
      <p style={SECTION_SUB}>How failed faxes are retried for {clientName}, your ★ priority numbers, and every retry: waiting to go, used up or cancelled, delivered by a retry, and the Retry log of every retry and resend with who did it.</p>

      {/* How retries work, and whether they're on */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={S.cardTitle}>Automatic retries</div>
          <span style={{ ...S.state, ...(data.retriesOn ? S.stateOn : S.stateOff) }}>{data.retriesOn ? 'ON' : 'OFF'}{allClients ? ' (all clients)' : ''}</span>
        </div>
        <ul style={S.howList}>
          {/* 3.75.0: the counts and delay are Sending Timings' */}
          <li>{tm.retries === 0 ? <>A fax that fails for a temporary reason (busy line, dropped call) is <strong>not retried</strong> automatically.</> : <>A fax that fails for a temporary reason (busy line, dropped call) is retried <strong>{tm.retriesWord}</strong>, {tm.retryFirst} later.</>}</li>
          <li>★ <strong>Priority numbers</strong> get <strong>{tm.priorityRetries === 1 ? '1 retry' : `${tm.priorityRetries} retries`}</strong>{tm.priorityRetries > 1 ? ' (or fewer, as set below)' : ''}, and are retried even when the number keeps failing.</li>
          <li>Retries go out only in your company’s <strong>office hours</strong> (Settings → Office Hours; standard: weekdays, 8 AM–5 PM Eastern); a retry due outside them waits until they next start.</li>
          <li>Don’t want to wait? <strong>Resend now</strong> sends a fax waiting for its retry straight away (the retry is cancelled).</li>
          <li>After the last retry it stops: <strong>Resend</strong> it below (a resend also gets {retryWords.replace(' automatic', '')}).</li>
          {!data.retriesOn && <li style={{ color: 'var(--t-c62828)' }}>Automatic retries are switched off{allClients ? '' : ' for your company'}; failed faxes aren’t retried until Fax Portal support switches them back on. You can still resend them.</li>}
        </ul>
      </div>

      {/* ★ Priority numbers: each client's own */}
      <PriorityNumbers stars={data.stars} allClients={allClients} canEdit={shows('fax.priority')} accountId={effectiveInfo?.accountId}
        clients={effectiveInfo?.accounts || []} busy={busy} onSave={(body, title, key) => call('/portal/control/client-priority', body, title, key)} />

      {/* The three lists */}
      <div style={{ ...PILL_ROW, margin: '18px 0 12px' }} role="tablist" aria-label="Retry lists">
        {[['pending', '⏳ Scheduled'], ['exhausted', '🔁 Used up or cancelled'], ['recovered', '✅ Delivered by a retry'], ['log', '📜 Retry log']].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={list === k} onClick={() => setList(k)} style={{ ...PILL, ...(list === k ? PILL_ON : {}) }}>{emoLabel(l)}{k === 'log' && !log ? '' : ` ${lists[k].length}`}</button>
        ))}
        <ShowSelect value={show} onChange={setShow} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}
          labelStyle={{ fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600 }} style={S.select} />
      </div>
      <p style={{ ...S.sub, margin: '0 0 8px' }}>{{
        pending: 'Failed faxes waiting for their automatic retry, and resends waiting for the line (one call at a time to a number).',
        exhausted: 'Failed faxes whose retries are used up or were cancelled, from the last 30 days. Resend sends one more attempt.',
        recovered: 'Faxes that failed first and were delivered by an automatic retry, in the last 30 days.',
        log: 'Every automatic retry, resend and cancelled retry of faxes sent in the last 30 days, newest first: when, who (or automatic), which attempt and how it went.',
      }[list]}</p>
      {list === 'log' ? <RetryLog log={log} rows={pageRows} allClients={allClients} sort={sort} onSort={onSort} phone={phone} /> : phone ? (
        // 3.51.0: phones: one card per fax
        <div style={S.cards}>
          {!pageRows.length ? <EmptyState emptyText={{ pending: 'No retries waiting right now.', exhausted: 'Nothing used up or cancelled in the last 30 days.', recovered: 'No faxes delivered by a retry in the last 30 days.' }[list]} />
            : pageRows.map(r => {
              const reason = list === 'recovered' ? r.firstFailure : r.failureReason;
              return (
                <div key={r.faxId} style={S.mCard}>
                  <div style={S.mTop}><span style={S.mWho}>To {formatFax(r.toNumber)}</span><span style={S.mWhen}>{whenLabel}: {r.line ? 'about ' : ''}{formatDate(list === 'pending' ? r.fireAt : list === 'recovered' ? r.deliveredAt : (r.updatedAt || r.sentAt))}</span></div>
                  <div style={S.mMeta}>{[allClients && r.accountName, r.fromEmail, r.fileName, r.pages != null && `${r.pages} pages`].filter(Boolean).join(' · ')}</div>
                  <div style={S.mMeta}>{r.line ? lineText(r) : list === 'pending' ? `Next attempt: ${r.nextAttempt}` : `Attempt #${r.attempt}`}{reason && <> · <code style={S.code}>{reason}</code></>}
                    {r.retryCancelled && <span style={{ color: 'var(--t-6a1b9a)' }}> · {r.retryCancelled === 'resent' ? 'user resent' : `retry cancelled${r.retryCancelled === 'admin' ? ' by Fax Portal support' : ''}`}</span>}
                    {allClients && r.accountOff && <span> · retries off: waiting</span>}</div>
                  {(list === 'pending' || list === 'exhausted') && (
                    <div style={S.mActions}>
                      {r.line && data.canSendNow && <button disabled={busy === `s:${r.queueId}`} onClick={() => sendNow(r)} style={S.actionBtn}><Emo e="↻" /> Send now</button>}
                      {list === 'pending' && !r.line && can('fax.resend') && <button disabled={busy === `r:${r.faxId}`} onClick={() => resendNow(r)} style={S.actionBtn}><Emo e="↻" /> Resend now</button>}
                      {list === 'pending' && !r.line && can('fax.cancelRetry') && <button disabled={busy === `c:${r.faxId}`} onClick={() => cancelRetry(r)} style={S.dangerBtn}>✕ Cancel</button>}
                      {list === 'exhausted' && can('fax.resend') && <button disabled={busy === `r:${r.faxId}`} onClick={() => resend(r)} style={S.actionBtn}><Emo e="↻" /> Resend</button>}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      ) : (
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}>
            <SortTh k="when" label={whenLabel} sort={sort} onSort={onSort} style={S.th} />
            {allClients && <SortTh k="client" label={clientWord} sort={sort} onSort={onSort} style={S.th} />}
            <SortTh k="by" label="Sent by" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="to" label="Sent to" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="file" label="File" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="pages" label="Pages" sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="attempt" label={list === 'pending' ? 'Next attempt' : 'Attempt'} sort={sort} onSort={onSort} style={S.th} />
            <SortTh k="reason" label={list === 'recovered' ? 'First failure' : 'Failure reason'} sort={sort} onSort={onSort} style={S.th} />
            {list !== 'recovered' && <th style={S.th}><span style={S.srOnly}>Actions</span></th>}
          </tr></thead>
          <tbody>
            {!pageRows.length ? <tr><td colSpan={9} style={S.empty}><EmptyState emptyText={{ pending: 'No retries waiting right now.', exhausted: 'Nothing used up or cancelled in the last 30 days.', recovered: 'No faxes delivered by a retry in the last 30 days.' }[list]} /></td></tr>
              : pageRows.map((r, i) => (
                <tr key={r.faxId} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)' }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{r.line ? 'about ' : ''}{formatDate(list === 'pending' ? r.fireAt : list === 'recovered' ? r.deliveredAt : (r.updatedAt || r.sentAt))}</td>
                  {allClients && <td style={{ ...S.td, fontSize: 12 }}>{r.accountName || '—'}{r.accountOff && <div style={S.meta}>retries off: waiting</div>}</td>}
                  <td style={{ ...S.td, color: 'var(--t-00695c)' }}>{r.fromEmail || '—'}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{formatFax(r.toNumber)}</td>
                  <td style={{ ...S.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fileName || ''}>{r.fileName || '—'}</td>
                  <td style={S.td}>{r.pages ?? '—'}</td>
                  <td style={S.td}>{r.line ? lineText(r) : list === 'pending' ? r.nextAttempt : `#${r.attempt}`}{r.retryCancelled && <div style={{ ...S.meta, color: 'var(--t-6a1b9a)' }}>{r.retryCancelled === 'resent' ? 'user resent' : `retry cancelled${r.retryCancelled === 'admin' ? ' by Fax Portal support' : ''}`}</div>}</td>
                  <td style={S.td}>{(list === 'recovered' ? r.firstFailure : r.failureReason) ? <code style={S.code}>{list === 'recovered' ? r.firstFailure : r.failureReason}</code> : '—'}</td>
                  {list === 'pending' && r.line && <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {data.canSendNow && <button disabled={busy === `s:${r.queueId}`} onClick={() => sendNow(r)} style={S.actionBtn} title="Send it now instead of waiting in line"><Emo e="↻" /> Send now</button>}
                  </td>}
                  {list === 'pending' && !r.line && <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {can('fax.resend') && <button disabled={busy === `r:${r.faxId}`} onClick={() => resendNow(r)} style={{ ...S.actionBtn, marginRight: 6 }} title="Send it now instead of waiting for the automatic retry"><Emo e="↻" /> Resend now</button>}
                    {can('fax.cancelRetry') && <button disabled={busy === `c:${r.faxId}`} onClick={() => cancelRetry(r)} style={S.dangerBtn}>✕ Cancel</button>}
                  </td>}
                  {list === 'exhausted' && <td style={{ ...S.td, textAlign: 'right' }}>{can('fax.resend') && <button disabled={busy === `r:${r.faxId}`} onClick={() => resend(r)} style={S.actionBtn}><Emo e="↻" /> Resend</button>}</td>}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      )}
      <Pager {...pager} label={list === 'log' ? 'entries' : 'faxes'} />
    </div>
  );
}

// Retry log rows (3.49.22, GET /portal/settings/fax/log)
const STOPPED = { 'destination-guard': 'the number kept failing', 'file-expired': 'the file is no longer kept', blocked: 'the number is blocked', resent: 'user resent' };   // 3.64.4 resent
const LOG_WHAT = r => (r.kind === 'auto' ? `Automatic retry (attempt ${r.attempt})` : r.kind === 'manual' ? (r.instead ? 'Resend now (instead of the retry)' : 'Resend')
  : r.kind === 'cancelled' ? 'Retry cancelled' : `Not retried: ${STOPPED[r.result] || r.result}`);
// 3.69.4: a Resend waiting for the line (Schedule rows and their text)
const lineText = r => `Resend${r.resentBy ? ` by ${r.resentBy}` : ''} · waiting for the line${r.position ? ` (${r.position} of ${r.of})` : ''}${r.slow ? ' · hard to reach' : ''}${r.hours ? ` · after office hours open` : ''}`;
const LOG_RESULT = {
  waiting: ['Waiting for the line', 'var(--t-e65100)', 'var(--c-fff3e0)'],   // 3.69.4
  delivered: ['Delivered', 'var(--t-2e7d32)', 'var(--c-e8f5e9)'], failed: ['Failed', 'var(--t-c62828)', 'var(--c-ffebee)'], sending: ['Sending', 'var(--t-1565c0)', 'var(--c-e3f2fd)'],
};
function RetryLog({ log, rows, allClients, sort, onSort, phone }) {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  if (!log) return <Loader label="Loading the retry log…" />;
  if (log.error) return <div style={S.error}>{log.error}</div>;
  // 3.51.0: phones: one card per entry
  if (phone) return (
    <div style={S.cards}>
      {!rows.length ? <EmptyState emptyText="No retries or resends in the last 30 days." /> : rows.map(r => {
        const [label, color, bg] = LOG_RESULT[r.result] || [];
        return (
          <div key={`${r.faxId}-${r.kind}`} style={S.mCard}>
            <div style={S.mTop}><span style={S.mWho}>{LOG_WHAT(r)}</span><span style={S.mWhen}>{formatDate(r.at)}</span></div>
            <div style={S.mMeta}>{[r.who, `to ${formatFax(r.toNumber)}`, allClients && r.accountName, r.fileName, r.pages != null && `${r.pages} pages`].filter(Boolean).join(' · ')}</div>
            {(label || r.reason) && <div style={{ ...S.mMeta, marginTop: 4 }}>{label && <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 10, color, background: bg }}>{label}</span>}
              {r.reason && <> <code style={S.code}>{r.reason}</code></>}</div>}
          </div>
        );
      })}
    </div>
  );
  return (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead><tr style={S.thead}>
          <SortTh k="when" label="When" sort={sort} onSort={onSort} style={S.th} />
          {allClients && <SortTh k="client" label={clientWord} sort={sort} onSort={onSort} style={S.th} />}
          <SortTh k="what" label="What" sort={sort} onSort={onSort} style={S.th} />
          <SortTh k="by" label="Who" sort={sort} onSort={onSort} style={S.th} />
          <SortTh k="to" label="Sent to" sort={sort} onSort={onSort} style={S.th} />
          <SortTh k="file" label="File" sort={sort} onSort={onSort} style={S.th} />
          <SortTh k="pages" label="Pages" sort={sort} onSort={onSort} style={S.th} />
          <SortTh k="result" label="Result" sort={sort} onSort={onSort} style={S.th} />
        </tr></thead>
        <tbody>
          {!rows.length ? <tr><td colSpan={8} style={S.empty}><EmptyState emptyText="No retries or resends in the last 30 days." /></td></tr>
            : rows.map((r, i) => {
              const [label, color, bg] = LOG_RESULT[r.result] || [];
              return (
                <tr key={`${r.faxId}-${r.kind}`} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', borderBottom: '1px solid var(--c-f0f0f0)' }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(r.at)}</td>
                  {allClients && <td style={{ ...S.td, fontSize: 12 }}>{r.accountName || '—'}</td>}
                  <td style={S.td}>{LOG_WHAT(r)}</td>
                  <td style={{ ...S.td, color: r.who === 'Automatic' ? 'var(--t-78909c)' : 'var(--t-00695c)' }}>{r.who}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{formatFax(r.toNumber)}</td>
                  <td style={{ ...S.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fileName || ''}>{r.fileName || '—'}</td>
                  <td style={S.td}>{r.pages ?? '—'}</td>
                  <td style={S.td}>{label ? <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 10, color, background: bg }}>{label}</span> : '—'}
                    {r.reason && <div style={S.meta}><code style={S.code}>{r.reason}</code></div>}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

// ★ Priority numbers: the client's own (Super Admins on "All clients": every
// client's). Retries 1 or 2, a label, remove, and add a number.
function PriorityNumbers({ stars, allClients, canEdit, accountId, clients, busy, onSave }) {
  const clientWord = useClientWord();   // 3.67.5: 'Company' for the people at a client
  const phone = usePhone();   // 3.51.0: each number stacked on phones
  // 3.75.0: up to Sending Timings' most for a ★ number (standard 2), which is also the default
  const most = useSendTimings().priorityRetries;
  const counts = Array.from({ length: most }, (_, i) => i + 1);
  const retryOptions = counts.map(n => <option key={n} value={n}>{n === 1 ? '1 retry' : `${n} retries`}</option>);
  const [draft, setDraft] = useState({ number: '', label: '', retries: 0, accountId: '' });   // 0 = the most
  const [edits, setEdits] = useState({});
  const keyOf = p => `${p.accountId}:${last10(p.number)}`;
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>★ Priority numbers</div>
      <div style={S.sub}>Numbers that matter most to {allClients ? 'each client' : 'your company'}: failed faxes to them get {most === 1 ? '1 retry' : `${most} retries (or fewer)`}, even when the number keeps failing. Star a number with ★ in Outbox, Logs or Contacts, or add it here.</div>
      {!stars.length ? <div style={{ ...S.sub, marginTop: 8 }}>No priority numbers yet.</div> : (
        <table style={{ ...S.table, marginTop: 8 }}>
          <tbody style={phone ? { display: 'block' } : undefined}>
            {stars.map(p => {
              const k = keyOf(p), e = edits[k] || {}, retries = e.retries ?? Math.min(p.retries || most, most), label = e.label ?? (p.label || '');
              const changed = retries !== Math.min(p.retries || most, most) || label !== (p.label || '');
              return (
                <tr key={k} style={{ borderBottom: '1px solid var(--c-eceff1)', ...(phone ? S.starRowPhone : {}) }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}><span style={{ color: 'var(--t-f9a825)' }}>★</span> <span style={{ fontFamily: 'monospace' }}>{formatFax(p.number)}</span>{allClients && <div style={S.meta}>{p.accountName}</div>}</td>
                  <td style={phone ? { ...S.td, flex: '1 1 100%', padding: '4px 0' } : S.td}>{canEdit ? <input value={label} onChange={ev => setEdits(x => ({ ...x, [k]: { ...e, label: ev.target.value } }))} placeholder="Label, e.g. Front desk" maxLength={40} style={phone ? { ...S.input, width: '100%' } : S.input} /> : (p.label || '—')}</td>
                  <td style={S.td}>{canEdit ? (
                    <select value={retries} onChange={ev => setEdits(x => ({ ...x, [k]: { ...e, retries: Number(ev.target.value) } }))} style={S.select}>
                      {retryOptions}
                    </select>) : (n => (n === 1 ? '1 retry' : `${n} retries`))(Math.min(p.retries || most, most))}</td>
                  <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{canEdit && <>
                    {changed && <button disabled={busy === `s:${k}`} onClick={() => onSave({ accountId: p.accountId, number: p.number, retries, label }, 'Priority number saved', `s:${k}`)} style={S.actionBtn}>Save</button>}
                    <button disabled={busy === `x:${k}`} onClick={() => onSave({ accountId: p.accountId, number: p.number, remove: true }, 'No longer a priority number', `x:${k}`)} style={{ ...S.dangerBtn, marginLeft: 6 }}>Remove</button>
                  </>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
          {allClients && (
            <select value={draft.accountId} onChange={e => setDraft({ ...draft, accountId: e.target.value })} style={S.select} aria-label={clientWord}>
              <option value="">Client…</option>
              {clients.filter(c => c.id !== 'all').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <input value={draft.number} onChange={e => setDraft({ ...draft, number: e.target.value })} placeholder="Fax number" inputMode="tel" style={{ ...S.input, width: phone ? '100%' : 170 }} aria-label="Fax number" />
          <input value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} placeholder="Label (optional)" maxLength={40} style={{ ...S.input, width: phone ? '100%' : 200 }} aria-label="Label" />
          <select value={draft.retries || most} onChange={e => setDraft({ ...draft, retries: Number(e.target.value) })} style={S.select} aria-label="Retries">
            {retryOptions}
          </select>
          <button disabled={busy === 'add' || last10(draft.number).length !== 10 || (allClients && !draft.accountId)}
            onClick={() => { onSave({ add: true, accountId: allClients ? draft.accountId : accountId, number: draft.number, label: draft.label, retries: draft.retries || most }, 'Priority number added', 'add'); setDraft({ number: '', label: '', retries: 0, accountId: draft.accountId }); }}
            style={S.primaryBtn}>★ Add</button>
        </div>
      )}
    </div>
  );
}

const S = {
  // 3.62.9: the Fax Settings heading, like Reminders' (Reminders.jsx title / sub)
  groupTitle: { margin: '0 0 4px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  groupSub: { margin: 0, color: 'var(--t-607d8b)', fontSize: 13, maxWidth: 720, lineHeight: 1.5 },
  // 3.51.0: phones
  cards: { display: 'flex', flexDirection: 'column', gap: 10 },
  mCard: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0e0e0)', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.05)' },
  mTop: { display: 'flex', alignItems: 'baseline', gap: '2px 8px', justifyContent: 'space-between', flexWrap: 'wrap' },
  mWho: { fontSize: 15, fontWeight: 700, color: 'var(--t-263238)', minWidth: 0, overflowWrap: 'anywhere' },
  mWhen: { fontSize: 11.5, color: 'var(--t-78909c)', flexShrink: 0, textAlign: 'right' },
  mMeta: { fontSize: 12.5, color: 'var(--t-607d8b)', marginTop: 3, overflowWrap: 'anywhere' },
  mActions: { display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  starRowPhone: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '8px 0' },
  sub: { color: 'var(--t-607d8b)', fontSize: 13, lineHeight: 1.5 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, fontSize: 13 },
  card: { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '14px 18px', marginTop: 14, boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.04)' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--t-00695c)', marginBottom: 4 },
  state: { padding: '3px 12px', borderRadius: 14, fontSize: 12, fontWeight: 700 },
  stateOn: { background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)' },
  stateOff: { background: 'var(--c-fce4ec)', color: 'var(--t-c62828)' },
  howList: { margin: '8px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--t-37474f)', lineHeight: 1.7 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-546e7a)' },
  th: { padding: '11px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  td: { padding: '9px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
  meta: { fontSize: 11, color: 'var(--t-78909c)', marginTop: 2 },
  code: { fontSize: 11, background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '1px 6px', borderRadius: 4 },
  input: { padding: '7px 10px', borderRadius: 8, border: '1.5px solid var(--c-cfd8dc)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' },
  select: { padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)' },
  actionBtn: { padding: '6px 12px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-b2dfdb)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  dangerBtn: { padding: '6px 12px', background: 'var(--c-ffffff)', color: 'var(--t-c62828)', border: '1.5px solid var(--c-ef9a9a)', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  primaryBtn: { padding: '8px 14px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  empty: { textAlign: 'center', padding: '30px 0', color: 'var(--t-90a4ae)', fontSize: 14 },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
};
